/**
 * Form Validation Utilities
 * Validation rules and helpers for datasource configuration forms
 */

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  min?: number;
  max?: number;
  minItems?: number;
  message?: string;
}

export interface ValidationErrors {
  [key: string]: string;
}

/**
 * Validate a single field against a rule
 */
export function validateField(
  value: any,
  rule: ValidationRule
): string | null {
  // Required check
  if (rule.required && (!value || value.length === 0)) {
    return rule.message || 'This field is required';
  }

  // Skip other validations if value is empty and not required
  if (!value) return null;

  // String length validation
  if (typeof value === 'string') {
    if (rule.minLength && value.length < rule.minLength) {
      return (
        rule.message ||
        `Must be at least ${rule.minLength} characters long`
      );
    }

    if (rule.maxLength && value.length > rule.maxLength) {
      return (
        rule.message ||
        `Must be no more than ${rule.maxLength} characters long`
      );
    }

    // Pattern validation
    if (rule.pattern && !rule.pattern.test(value)) {
      return rule.message || 'Invalid format';
    }
  }

  // Number validation
  if (typeof value === 'number') {
    if (rule.min !== undefined && value < rule.min) {
      return rule.message || `Must be at least ${rule.min}`;
    }

    if (rule.max !== undefined && value > rule.max) {
      return rule.message || `Must be no more than ${rule.max}`;
    }
  }

  // Array validation
  if (Array.isArray(value)) {
    if (rule.minItems && value.length < rule.minItems) {
      return (
        rule.message || `Must select at least ${rule.minItems} item(s)`
      );
    }
  }

  return null;
}

/**
 * Validate an object against a schema
 */
export function validateForm<T extends Record<string, any>>(
  data: T,
  schema: Record<keyof T, ValidationRule>
): ValidationErrors {
  const errors: ValidationErrors = {};

  (Object.keys(schema) as Array<keyof T>).forEach((key) => {
    const error = validateField(data[key], schema[key]);
    if (error) {
      errors[key as string] = error;
    }
  });

  return errors;
}

/**
 * Odoo form validation schema
 */
export const odooValidationSchema = {
  url: {
    required: true,
    pattern: /^https?:\/\/.+\.odoo\.com$/,
    message:
      'Must be a valid Odoo URL (e.g., https://yourcompany.odoo.com)',
  },
  database: {
    required: true,
    minLength: 3,
    message: 'Database name must be at least 3 characters',
  },
  username: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Must be a valid email address',
  },
  apiKey: {
    required: true,
    minLength: 8,
    message: 'API key must be at least 8 characters',
  },
};

/**
 * Salesforce form validation schema (JWT Bearer Authentication)
 */
export const salesforceValidationSchema = {
  orgUrl: {
    required: true,
    pattern: /^https:\/\/(login|test)\.salesforce\.com$/,
    message: 'Must be a valid Salesforce login URL',
  },
  clientId: {
    required: true,
    minLength: 20,
    message: 'Client ID (Consumer Key) must be at least 20 characters',
  },
  username: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Must be a valid email address',
  },
  privateKey: {
    required: true,
    pattern: /-----BEGIN (RSA |PRIVATE )?PRIVATE KEY-----[\s\S]+-----END (RSA |PRIVATE )?PRIVATE KEY-----/,
    message: 'Must be a valid PEM-formatted private key',
  },
};

/**
 * SAP form validation schema
 */
export const sapValidationSchema = {
  instanceUrl: {
    required: true,
    pattern: /^https?:\/\/.+/,
    message: 'Must be a valid URL',
  },
  username: {
    required: true,
    minLength: 3,
    message: 'Username is required (min 3 characters)',
  },
  password: {
    required: true,
    minLength: 8,
    message: 'Password must be at least 8 characters',
  },
  systemId: {
    required: false,
    pattern: /^[A-Z0-9]{3}$/,
    message:
      'System ID must be 3 alphanumeric characters (if provided)',
  },
};

/**
 * Sync settings validation schema
 */
export const syncSettingsValidationSchema = {
  name: {
    required: true,
    minLength: 3,
    maxLength: 255,
    message: 'Name must be between 3 and 255 characters',
  },
  syncFrequency: {
    required: true,
    min: 5,
    max: 1440,
    message: 'Frequency must be between 5 minutes and 24 hours',
  },
  allowedRoles: {
    required: true,
    minItems: 1,
    message: 'At least one role must have access',
  },
};

/**
 * Check if form has validation errors
 */
export function hasErrors(errors: ValidationErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Email validation
 */
export function isValidEmail(email: string): boolean {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email);
}

/**
 * URL validation
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
